import { FC, ReactNode } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classnames from 'classnames';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { ViewInRoomImage } from 'dibs-view-in-room/exports/ViewInRoomImage';
import { SeoLink } from 'dibs-elements/exports/SeoLink';

import styles from '../styles.scss';
import { ThumbImage, ImageProps } from './ThumbImage';

import { ProductSwiper_item as ProductSwiperProp } from './__generated__/ProductSwiper_item.graphql';

type Props = {
    item: ProductSwiperProp;
    wasHovered: boolean;
    swiperWasClicked: boolean;
    isInitiallyVisibleTile: boolean;
    arrowsVisible: boolean;
    isMobile: boolean;
    itemsPerRow: number;
    index: number;
    onPageChange: (nextPage: number) => void;
    imageProps: ImageProps;
    onSwipeStart?: () => void;
    onSwipeEnd?: () => void;
    hasSingleImage: boolean;
    showViewInRoom?: boolean;
};

const ProductSwiperComponent: FC<Props> = props => {
    const {
        item,
        wasHovered,
        swiperWasClicked,
        isInitiallyVisibleTile,
        arrowsVisible,
        isMobile,
        index: tileIndex,
        onPageChange,
        imageProps,
        onSwipeStart,
        onSwipeEnd,
        hasSingleImage,
        /**
         * if we want to show view in room images we also need to provide `viewInRoomLimit` graphql value to the
         * ProductSwiper_item fragment. Pass `null` to fetch all images.
         */
        showViewInRoom,
    } = props;

    const carouselPhotos = item?.carouselPhotos || [];
    const viewInRoomPhotos = item?.viewInRoomPhotos || [];
    const mainImagePaths = {
        smallPath: carouselPhotos?.[0]?.smallPath,
        masterOrZoomPath: carouselPhotos?.[0]?.masterOrZoomPath,
    };

    const imageContainerClassNames = classnames(
        hasSingleImage ? styles.imageContainer : styles.swiperImageContainer,
        styles[imageProps.imageSize]
    );

    let globalIndex = 0;
    const images = carouselPhotos.reduce<ReactNode[]>((acc, photo) => {
        if (photo) {
            acc.push(
                <ThumbImage
                    {...imageProps}
                    useEagerImageLoad={
                        (isInitiallyVisibleTile && globalIndex === 0) ||
                        wasHovered ||
                        swiperWasClicked
                    }
                    photo={photo}
                    index={globalIndex}
                    key={`product-thumb-image-${tileIndex}-${globalIndex}`}
                    containerClassName={imageContainerClassNames}
                />
            );
            globalIndex += 1;
        }

        return acc;
    }, []);

    if (showViewInRoom) {
        const link = imageProps.disableLinks
            ? { path: '', onClick: undefined }
            : { path: imageProps.pdpUrl, onClick: imageProps.onClick };

        images.push(
            ...viewInRoomPhotos.reduce<ReactNode[]>((acc, photo) => {
                if (photo && item) {
                    acc.push(
                        <div
                            key={`product-view-in-room-image-${tileIndex}-${globalIndex}`}
                            className={classnames(
                                styles.viewInRoomContainer,
                                styles[imageProps.imageSize]
                            )}
                        >
                            <SeoLink
                                dataTn="view-in-room-image-link"
                                linkData={{ path: link.path, isLinkable: false }}
                                onClick={link.onClick}
                            >
                                <ViewInRoomImage
                                    {...imageProps}
                                    useEagerImageLoad={
                                        (isInitiallyVisibleTile && globalIndex === 0) ||
                                        wasHovered ||
                                        swiperWasClicked
                                    }
                                    item={item}
                                    viewInRoomPhoto={photo}
                                    mainImagePaths={mainImagePaths}
                                    className={styles.viewInRoomImage}
                                />
                            </SeoLink>
                        </div>
                    );
                    globalIndex += 1;
                }
                return acc;
            }, [])
        );
    }

    if (hasSingleImage) {
        return <>{images[0]}</>;
    }

    return (
        <Swiper
            itemsPerPage={1}
            showArrows={!isMobile}
            hasInfiniteLoop
            onPageChange={onPageChange}
            classNames={{
                item: styles.swiperImageListItem,
                prevArrow: [
                    styles.bumpSpecificity,
                    styles.swiperArrow,
                    styles.leftArrow,
                    arrowsVisible ? styles.arrowVisible : null,
                ],
                nextArrow: [
                    styles.bumpSpecificity,
                    styles.swiperArrow,
                    styles.rightArrow,
                    arrowsVisible ? styles.arrowVisible : null,
                ],
            }}
            onSwipeStart={onSwipeStart}
            onSwipeEnd={onSwipeEnd}
        >
            {images}
        </Swiper>
    );
};

export const ProductSwiper = createFragmentContainer(ProductSwiperComponent, {
    item: graphql`
        fragment ProductSwiper_item on Item
        @argumentDefinitions(
            photosLimit: { type: "Int", defaultValue: 3 }
            viewInRoomLimit: { type: "Int", defaultValue: 0 }
        ) {
            ...ViewInRoomImage_item
            carouselPhotos: photos(limit: $photosLimit) {
                ...ThumbImage_photo
                smallPath
                masterOrZoomPath
            }
            viewInRoomPhotos(limit: $viewInRoomLimit) {
                ...ViewInRoomImage_viewInRoomPhoto
            }
        }
    `,
});
